import { z } from "zod";
import { manuscriptChangeApproveMetadataSchema } from "../manuscriptChangeApproveSignals";
import { STORY_BIBLE_CTA_TYPES } from "./storyBibleCtaTypes";

const agenticChatModeSchema = z.enum(["write", "chat"]);

const ctaActionPayloadSchema = z.object({
  type: z.enum(STORY_BIBLE_CTA_TYPES),
  targetSurface: z.literal("story_bible_outline_summary").optional(),
  targetId: z.string().optional(),
  targetName: z.string().optional(),
  customInstructions: z.string().optional(),
  outlineFramework: z.string().optional(),
  characterCount: z.union([z.number(), z.literal("default")]).optional(),
  characterId: z.string().optional(),
  entryId: z.string().optional(),
  traitId: z.string().optional(),
  traitLabel: z.string().optional(),
  role: z.string().optional(),
  roleLabel: z.string().optional(),
  roleExplicitlySelected: z.boolean().optional(),
  fields: z.array(z.object({
    id: z.string().optional(),
    label: z.string(),
    value: z.unknown().optional(),
  }).passthrough()).optional(),
}).passthrough();

const agenticWriteButtonSettingsSchema = z.object({
  writeModel: z.string().optional(),
  wormholeOutputLength: z.number().optional(),
  wormholeOutputCards: z.number().optional(),
  creativitySliderPosition: z.number().optional(),
  writeMode: z.enum(["auto", "guided", "tone"]).optional(),
  tone: z.string().nullable().optional(),
  memory: z.string().optional(),
  memoryEnabled: z.boolean().optional(),
}).passthrough();

const nullishBoolean = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.boolean().optional(),
);

/** Partial hints are valid on the wire (e.g. caret-only chips with only plainTextStart). */
const editorSelectionHintSchema = z.object({
  documentId: z.string(),
  pmFrom: z.number().optional(),
  pmTo: z.number().optional(),
  plainTextStart: z.number().optional(),
  plainTextEnd: z.number().optional(),
}).passthrough();

const manuscriptSelectionTargetSchema = z.object({
  documentId: z.string().min(1),
  passageText: z.string().min(8),
  paragraphs: z.array(z.string()).optional(),
  pmFrom: z.number().optional(),
  pmTo: z.number().optional(),
  plainTextStart: z.number().optional(),
  plainTextEnd: z.number().optional(),
  markdownRange: z.object({
    start: z.number(),
    end: z.number(),
  }).optional(),
  markdownQuotedText: z.string().optional(),
  anchorId: z.string().optional(),
}).passthrough();

const manuscriptChangeCapabilitySchema = z.object({
  capabilityVersion: z.literal(2),
  canReview: z.boolean(),
  canAcknowledgeVisibility: z.boolean(),
}).strict();

const userMessageMetadataSchema = z.object({
  appendedDocumentMentions: nullishBoolean,
  questionAnswer: nullishBoolean,
  feedbackTypesPickerSubmission: nullishBoolean,
  feedbackRetry: nullishBoolean,
  dislikedAssistantMessageId: z.string().optional(),
  feedbackText: z.string().optional(),
  manuscriptChangeApprove: manuscriptChangeApproveMetadataSchema.optional(),
}).passthrough();

const planStepStatusSchema = z.enum(["pending", "in_progress", "completed", "cancelled"]);
const planStatusSchema = z.enum(["proposed", "in_progress", "paused", "completed", "cancelled"]);

const creditCostEstimateRangeSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  avg: z.number().optional(),
}).passthrough();

const planStepSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  item_count: z.number().optional(),
  depends_on_step_ids: z.array(z.string()).optional(),
  postconditions: z.array(z.string()).optional(),
  verification: z.object({
    required_tool_names: z.array(z.string()).optional(),
    min_successful_mutations: z.number().optional(),
  }).passthrough().optional(),
  generated_by: z.enum(["planner", "repair"]).optional(),
  resume_state: z.object({
    mutation_success_count: z.number().optional(),
    successful_tool_names: z.array(z.string()).optional(),
    successful_mutation_domains: z.array(z.enum(["character", "worldbuilding", "outline", "meta"])).optional(),
    successful_read_targets: z.array(z.string()).optional(),
  }).passthrough().optional(),
  status: planStepStatusSchema,
  summary: z.string().optional(),
}).passthrough();

/** Client-edited plan payload accepted on /message when editedPlanApproved is set. */
export const editedPlanSchema = z.object({
  id: z.string().min(1),
  description: z.string(),
  specialConsiderations: z.string().optional(),
  steps: z.array(planStepSchema).min(1),
  estimatedCreditCost: z.number().optional(),
  creditCostEstimateRange: creditCostEstimateRangeSchema.optional(),
  estimates: z.object({
    estimatedCreditCost: z.number().optional(),
    creditCostEstimateRange: creditCostEstimateRangeSchema.optional(),
  }).passthrough().optional(),
  status: planStatusSchema,
  currentStepIndex: z.number().int().nonnegative(),
}).passthrough();

/**
 * Wire body for POST /api/ai/agenticChat/message.
 * Derive AgenticChatRequestBody via z.infer so the type and validator cannot drift.
 */
export const agenticChatRequestBodySchema = z.object({
  projectId: z.string().min(1),
  chatId: z.string().min(1).optional(),
  message: z.string().min(1),
  pastedExcerpts: z.array(z.string()).optional(),
  passage: z.string().optional(),
  passageHtml: z.string().optional(),
  documentText: z.string().optional(),
  selectionAnnotationIds: z.array(z.string()).optional(),
  commentSidebarOrderIds: z.array(z.string()).optional(),
  model: z.string().optional(),
  mode: agenticChatModeSchema.optional(),
  documentId: z.string().optional(),
  openDocumentId: z.string().optional(),
  isStoryBibleActive: nullishBoolean,
  activeManuscriptCommentId: z.string().optional(),
  hidden: z.boolean().optional(),
  userMessageMetadata: userMessageMetadataSchema.optional(),
  ctaAction: ctaActionPayloadSchema.optional(),
  resendOfMessageId: z.string().optional(),
  resendIntent: z.enum(['retry', 'edit']).optional(),
  clientRequestId: z.string().max(128).optional(),
  priorClientRequestIds: z.array(z.string().max(128)).max(128).optional(),
  replacesClientRequestId: z.string().max(128).optional(),
  targetClientRequestId: z.string().max(128).optional(),
  parentMessageId: z.string().nullable().optional(),
  editorSelectionHint: editorSelectionHintSchema.optional(),
  selectionTarget: manuscriptSelectionTargetSchema.optional(),
  manuscriptChangeCapability: manuscriptChangeCapabilitySchema.optional(),
  editedPlanApproved: z.boolean().optional(),
  editedPlan: editedPlanSchema.optional(),
  agenticWriteButtonSettings: agenticWriteButtonSettingsSchema.nullable().optional(),
  outlineSaveFailed: z.boolean().optional(),
  skipCachedEditorSelectionMerge: z.boolean().optional(),
  timeZone: z.string().optional(),
});

export type AgenticChatRequestBody = z.infer<typeof agenticChatRequestBodySchema>;
export type AgenticChatMode = z.infer<typeof agenticChatModeSchema>;
export type CTAActionPayload = z.infer<typeof ctaActionPayloadSchema>;
export type AgenticWriteButtonSettingsPayload = z.infer<typeof agenticWriteButtonSettingsSchema>;

const planEndpointBaseSchema = z.object({
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  planId: z.string().min(1),
  model: z.string().optional(),
  mode: agenticChatModeSchema.optional(),
  agenticWriteButtonSettings: agenticWriteButtonSettingsSchema.nullable().optional(),
});

/** Coerce a single id (legacy clients) into an array before validating. */
const removedStepIdsSchema = z.preprocess(
  (value) => {
    if (value == null || value === "") return undefined;
    return Array.isArray(value) ? value : [value];
  },
  z.array(z.union([z.string(), z.number()])).optional(),
);

export const planApproveRequestBodySchema = planEndpointBaseSchema.extend({
  removedStepIds: removedStepIdsSchema,
});

export const planResumeRequestBodySchema = planEndpointBaseSchema;

export const planCancelRequestBodySchema = z.object({
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  planId: z.string().min(1),
  mode: agenticChatModeSchema.optional(),
});

export const planPauseRequestBodySchema = planCancelRequestBodySchema;

export const planDuplicateRequestBodySchema = z.object({
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  sourcePlanId: z.string().min(1),
});

/** Historical / client revert bundles omit keys; restore paths already default missing arrays. */
export const snapshotBundleSchema = z.object({
  outlineSnapshotId: z.string().nullable().optional(),
  outlineId: z.string().nullable().optional(),
  characterSnapshots: z.record(z.string()).optional(),
  worldbuildingSnapshots: z.record(z.string()).optional(),
  metaSnapshot: z.record(z.unknown()).nullable().optional(),
  createdCharacterIds: z.array(z.string()).optional(),
  createdWorldbuildingIds: z.array(z.string()).optional(),
}).passthrough();

export const revertRequestBodySchema = z.object({
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  messageId: z.string().min(1),
  assistantMessageId: z.string().optional(),
  snapshotBundle: snapshotBundleSchema.nullable().optional(),
});

export type PlanApproveRequestBody = z.infer<typeof planApproveRequestBodySchema>;
export type PlanResumeRequestBody = z.infer<typeof planResumeRequestBodySchema>;
export type PlanCancelRequestBody = z.infer<typeof planCancelRequestBodySchema>;
export type PlanPauseRequestBody = z.infer<typeof planPauseRequestBodySchema>;
export type PlanDuplicateRequestBody = z.infer<typeof planDuplicateRequestBodySchema>;
export type RevertRequestBody = z.infer<typeof revertRequestBodySchema>;

const manuscriptChangeTargetSchema = z.object({
  changeId: z.string().min(1),
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  documentId: z.string().min(1),
  editorSelectionHint: editorSelectionHintSchema.optional(),
  clientPassage: z.string().optional(),
  clientDocumentBody: z.string().optional(),
  clientRevision: z.string().min(1).optional(),
}).strict();

const reviewRewriteOperationSchema = z.discriminatedUnion("scope", [
  z.object({
    kind: z.literal("review_rewrite"),
    scope: z.literal("selection"),
    instruction: z.string().min(1),
    selection: z.object({
      start: z.number().int().nonnegative(),
      end: z.number().int().nonnegative(),
      quotedText: z.string(),
      anchorId: z.string().min(1).optional(),
    }).strict(),
  }).strict(),
  z.object({
    kind: z.literal("review_rewrite"),
    scope: z.literal("chapter"),
    instruction: z.string().min(1),
    chapterTitle: z.string().min(1),
  }).strict(),
  z.object({
    kind: z.literal("review_rewrite"),
    scope: z.literal("document"),
    instruction: z.string().min(1),
  }).strict(),
]);

const anchoredInsertOperationSchema = z.union([
  z.object({
    kind: z.enum(["insert_before", "insert_after"]),
    text: z.string(),
    anchorText: z.string().min(1),
  }).strict(),
  z.object({
    kind: z.enum(["insert_before", "insert_after"]),
    text: z.string(),
    anchorId: z.string().min(1),
  }).strict(),
]);

export const manuscriptChangeOperationSchema = z.union([
  reviewRewriteOperationSchema,
  z.object({
    kind: z.literal("literal_find_replace"),
    searchText: z.string().min(1),
    replacementText: z.string(),
    occurrence: z.number().int().positive().optional(),
    searchNearPlainTextOffset: z.number().int().nonnegative().optional(),
    anchorId: z.string().min(1).optional(),
  }).strict(),
  z.object({
    kind: z.literal("replace_all"),
    content: z.string(),
  }).strict(),
  anchoredInsertOperationSchema,
  z.object({ kind: z.literal("append"), text: z.string() }).strict(),
  z.object({ kind: z.literal("prepend"), text: z.string() }).strict(),
]);

export const manuscriptChangeApproveRequestSchema =
  manuscriptChangeTargetSchema;
export const manuscriptChangeDeclineRequestSchema =
  manuscriptChangeTargetSchema;
export const manuscriptChangeRetryRequestSchema =
  manuscriptChangeTargetSchema;
export const manuscriptChangeVisibleRequestSchema =
  manuscriptChangeTargetSchema.extend({
    visibleRevision: z.string().min(1),
  }).strict();
export const manuscriptChangeSetApplyRequestSchema = z.object({
  changeSetId: z.string().min(1),
  proposalGroupId: z.string().min(1),
  optionId: z.string().min(1),
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  clientDocumentBody: z.string().optional(),
  clientRevision: z.string().min(1).optional(),
}).strict();
export const manuscriptChangeSetUndoRequestSchema = z.object({
  changeSetId: z.string().min(1),
  chatId: z.string().min(1),
  projectId: z.string().min(1),
  clientDocumentBody: z.string().optional(),
  clientRevision: z.string().min(1).optional(),
}).strict();
export const manuscriptChangeModifyRequestSchema =
  manuscriptChangeTargetSchema.extend({
    operation: manuscriptChangeOperationSchema,
  }).strict();
export const manuscriptChangeConflictResolutionRequestSchema =
  manuscriptChangeTargetSchema.extend({
    resolution: z.enum(["cancel", "create_successor"]),
    operation: manuscriptChangeOperationSchema.optional(),
  }).strict().superRefine((value, ctx) => {
    if (value.resolution === "create_successor" && !value.operation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["operation"],
        message: "operation is required to create a successor",
      });
    }
    if (value.resolution === "cancel" && value.operation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["operation"],
        message: "operation is not allowed when cancelling",
      });
    }
  });

export const manuscriptChangeActionResponseSchema = z.object({
  rejectionStage: z.string().optional(),
  matchCount: z.number().int().nonnegative().optional(),
  changeId: z.string().min(1),
  outcome: z.enum([
    "applied",
    "declined",
    "superseded",
    "conflict",
    "failed",
    "in_progress",
    "not_applicable",
    "not_found",
    "visible",
    "undone",
    "needs_review_again",
    "could_not_apply",
  ]),
  status: z.string().optional(),
  successorId: z.string().optional(),
  noOp: z.boolean().optional(),
  revision: z.string().optional(),
  retryable: z.boolean().optional(),
  code: z.string().optional(),
  message: z.string().optional(),
  baseRevision: z.string().optional(),
  currentRevision: z.string().optional(),
  idempotent: z.boolean().optional(),
}).strict();

export const manuscriptChangeSetActionResponseSchema = z.object({
  changeSetId: z.string().min(1),
  status: z.enum([
    "applied",
    "partially_applied",
    "partially_undone",
    "needs_review_again",
    "could_not_apply",
    "undone",
  ]),
  reason: z.string().optional(),
  message: z.string().optional(),
  documents: z.array(z.object({
    documentId: z.string().min(1),
    changeId: z.string().min(1),
    status: z.string().min(1),
    revision: z.string().optional(),
  }).strict()).optional(),
}).strict();

export type ManuscriptChangeApproveRequest = z.infer<typeof manuscriptChangeApproveRequestSchema>;
export type ManuscriptChangeDeclineRequest = z.infer<typeof manuscriptChangeDeclineRequestSchema>;
export type ManuscriptChangeRetryRequest = z.infer<typeof manuscriptChangeRetryRequestSchema>;
export type ManuscriptChangeVisibleRequest = z.infer<typeof manuscriptChangeVisibleRequestSchema>;
export type ManuscriptChangeSetApplyRequest = z.infer<typeof manuscriptChangeSetApplyRequestSchema>;
export type ManuscriptChangeSetUndoRequest = z.infer<typeof manuscriptChangeSetUndoRequestSchema>;
export type ManuscriptChangeSetActionResponse = z.infer<typeof manuscriptChangeSetActionResponseSchema>;
export type ManuscriptChangeModifyRequest = z.infer<typeof manuscriptChangeModifyRequestSchema>;
export type ManuscriptChangeConflictResolutionRequest =
  z.infer<typeof manuscriptChangeConflictResolutionRequestSchema>;
export type ManuscriptChangeActionResponse =
  z.infer<typeof manuscriptChangeActionResponseSchema>;
